import { apiClient } from './api';
import { ApiResponse } from '@/types';
import type {
  Tree,
  CreateTreeRequest,
  DonationRequest,
  Donation,
} from './treesApi';
import { getScenariosWithPurchaseStatus } from '@/constants/capsuleScenarios';

export interface CapsuleInvite {
  id: string;
  capsule: string;
  from_user: {
    id: string;
    username: string;
    first_name?: string;
    last_name?: string;
    avatar?: string | null;
  };
  to_user: {
    id: string;
    username: string;
    first_name?: string;
    last_name?: string;
    avatar?: string | null;
  };
  created_at: string;
  accepted: boolean;
  declined?: boolean;
  is_read: boolean;
}

export interface Capsule {
  id: string;
  title: string;
  description?: string;
  image?: string;
  imagePreview?: string;
  content: ContentItem[];
  scenario?: ScenarioConfig;
  meta: {
    createdAt: string;
    firstOpenedAt?: string;
    scheduledAt?: string;
    isOpened: boolean;
    activationType?: 'manual' | 'scheduled' | 'custodian';
  };
  author: {
    id: string;
    name: string;
    avatar?: string;
    isAdministrator?: boolean;
    is_followed?: boolean;
  };
  purchased_scenarios?: string[];
  tree?: {
    id?: string;
    data?: Tree;
  };
  engagement: {
    comments: number;
  };
  privacy: {
    level: 'public' | 'coauthors' | 'selected';
    allowedUserIds?: string[];
    coauthors?: {
      id: string;
      name?: string;
      avatar?: string;
    }[];
  };
}

export interface ScenarioReactionPosition {
  top: number;
  left: number;
}

export interface ScenarioReactionReply {
  id: string;
  text: string;
  author: CommentAuthor;
  createdAt: string;
}

export interface ScenarioReaction {
  id: string;
  text: string;
  author: CommentAuthor;
  createdAt: string;
  position?: ScenarioReactionPosition;
  commentObject?: string;
  replies?: ScenarioReactionReply[];
  isGuest?: boolean;
}

export interface ScenarioConfig {
  id: string;
  backgroundUrl: string;
  backgroundPreviewUrl?: string;
  commentObject: string;
  comments?: ScenarioReaction[];
}

export interface ContentItem {
  id: string;
  type: 'image' | 'video' | 'gif' | 'link' | 'audio' | 'text';
  url?: string;
  previewUrl?: string;
  text?: string;
  caption?: string;
  title?: string;
  description?: string;
  order: number;
}

export interface CreateCapsuleRequest {
  title?: string;
  description?: string;
  content?: string;
  privacy?: string;
  privacy_level?: 'public' | 'coauthors' | 'selected';
  activationType?: 'manual' | 'scheduled' | 'custodian';
  scheduledAt?: string;
  allowedUserIds?: number[];
  allowedUserEmails?: string[];
  coauthorsIds?: number[];
  coauthorsEmails?: string[];
  activation_policy?:
    | 'immediate'
    | 'by_request'
    | 'scheduled_time'
    | 'after_death';
  scheduled_at?: string;
  death_offset_days?: number;
  text?: string;
  image?: string;
  video?: string;
  scenario?: string;
}

export interface UpdateCapsuleRequest {
  title?: string;
  description?: string;
  content?: string;
  privacy?: string;
  privacy_level?: 'public' | 'coauthors' | 'selected';
  activationType?: 'manual' | 'scheduled' | 'custodian';
  scheduledAt?: string;
  allowedUserIds?: number[];
  allowedUserEmails?: string[];
  coauthorsIds?: number[];
  coauthorsEmails?: string[];
  activation_policy?:
    | 'immediate'
    | 'by_request'
    | 'scheduled_time'
    | 'after_death';
  scheduled_at?: string;
  death_offset_days?: number;
  text?: string;
  image?: string;
  video?: string;
}

export interface CommentAuthor {
  id: string;
  name: string;
  avatar?: string;
}

export interface Comment {
  id: string;
  text: string;
  author: CommentAuthor;
  createdAt: string;
  updatedAt?: string;
  parentId?: string;
  replies?: Comment[];
  position?: { top: number; left: number };
  comment_object?: string | number;
  commentObject?: string | number;
}

export interface CreateCommentRequest {
  text: string;
  position?: {
    top: number;
    left: number;
  };
  parentId?: string;
  comment_object?: string | number;
}

export interface UpdateCommentRequest {
  text: string;
}

export interface CommentListResponse {
  comments: Comment[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface CapsuleListResponse {
  capsules: Capsule[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface CapsuleApiResponse {
  id: string;
  title: string;
  description?: string;
  image?: string | null;
  imagePreview?: string | null;
  content?: string;
  scenario?: string;
  meta?: string;
  author:
    | string
    | {
        id: string;
        name?: string;
        avatar?: string | null;
        is_administrator?: boolean;
        is_followed?: boolean;
      };
  tree?: string;
  comment_count?: number;
  engagement?: string;
  privacy?:
    | {
        level: 'public' | 'coauthors' | 'selected';
        allowedUserIds?: string[];
        coauthors?: {
          id: string;
          name?: string;
          avatar?: string | null;
        }[];
      }
    | string;
  activation_policy?:
    | 'immediate'
    | 'by_request'
    | 'scheduled_time'
    | 'after_death';
}

function mapApiCapsuleToCapsule(apiCapsule: CapsuleApiResponse): Capsule {
  const parseJsonField = (field: any): any => {
    if (typeof field === 'string') {
      try {
        return JSON.parse(field);
      } catch {
        return field;
      }
    }
    return field;
  };

  const content = parseJsonField(apiCapsule.content) || [];
  let scenario = apiCapsule.scenario
    ? parseJsonField(apiCapsule.scenario)
    : undefined;

  if (scenario && scenario.id) {
    const scenarioId = String(scenario.id);
    const capsuleId = String(apiCapsule.id);

    const isInvalidScenarioId =
      scenarioId === capsuleId || !scenarioId.startsWith('scenario-');

    if (isInvalidScenarioId) {
      const scenariosWithStatus = getScenariosWithPurchaseStatus();
      const defaultScenario = scenariosWithStatus.find(
        (s: { unlocked: boolean }) => s.unlocked
      );
      if (defaultScenario) {
        scenario = {
          ...scenario,
          id: defaultScenario.id,
          backgroundUrl:
            scenario.backgroundUrl && scenario.backgroundUrl !== null
              ? scenario.backgroundUrl
              : defaultScenario.background,
          commentObject:
            scenario.commentObject &&
            scenario.commentObject !== null &&
            scenario.commentObject !== 'capsule' &&
            (scenario.commentObject.startsWith('/') ||
              scenario.commentObject.startsWith('http'))
              ? scenario.commentObject
              : defaultScenario.commentObject,
        };
      }
    }
  }

  const meta = parseJsonField(apiCapsule.meta) || {};
  const engagementParsed = parseJsonField(apiCapsule.engagement) || {
    comments: 0,
  };
  const commentsCount =
    typeof apiCapsule.comment_count === 'number'
      ? apiCapsule.comment_count
      : (engagementParsed.comments ?? 0);

  const scheduledAt = meta.scheduledAt ?? meta.scheduled_at;

  const rawPolicy = apiCapsule.activation_policy || meta.activation_policy;
  const activationType: 'manual' | 'scheduled' | 'custodian' | undefined =
    rawPolicy
      ? rawPolicy === 'after_death'
        ? 'custodian'
        : rawPolicy === 'scheduled_time'
          ? 'scheduled'
          : 'manual'
      : undefined;

  const authorId =
    typeof apiCapsule.author === 'string'
      ? apiCapsule.author
      : (apiCapsule.author as any)?.id || String(apiCapsule.author || '');
  const authorName =
    typeof apiCapsule.author === 'object' && apiCapsule.author
      ? (apiCapsule.author as any).username ||
        (apiCapsule.author as any).name ||
        ''
      : '';
  const authorAvatar =
    typeof apiCapsule.author === 'object' && apiCapsule.author
      ? (apiCapsule.author as any).avatar || undefined
      : undefined;
  const authorIsAdmin =
    typeof apiCapsule.author === 'object' && apiCapsule.author
      ? Boolean((apiCapsule.author as any).is_administrator)
      : false;
  const authorIsFollowed =
    typeof apiCapsule.author === 'object' && apiCapsule.author
      ? Boolean((apiCapsule.author as any).is_followed)
      : undefined;

  const apiPrivacy = apiCapsule.privacy;
  const privacyLevel: 'public' | 'coauthors' | 'selected' =
    typeof apiPrivacy === 'string'
      ? (apiPrivacy as 'public' | 'coauthors' | 'selected')
      : (apiPrivacy?.level as 'public' | 'coauthors' | 'selected');

  const allowedUserIds =
    typeof apiPrivacy === 'object' && apiPrivacy
      ? apiPrivacy.allowedUserIds
      : undefined;

  const coauthors =
    typeof apiPrivacy === 'object' && apiPrivacy
      ? apiPrivacy.coauthors
      : undefined;

  const normalizedCoauthors =
    coauthors?.map(c => ({
      id: c.id,
      name: c.name,
      avatar: c.avatar ?? undefined,
    })) ?? [];

  return {
    id: apiCapsule.id,
    title: apiCapsule.title,
    description: apiCapsule.description,
    image: apiCapsule.image ?? undefined,
    imagePreview: apiCapsule.imagePreview ?? undefined,
    content: Array.isArray(content) ? content : [],
    scenario: scenario,
    meta: {
      createdAt: meta.createdAt || new Date().toISOString(),
      firstOpenedAt: meta.firstOpenedAt,
      scheduledAt: scheduledAt ?? undefined,
      isOpened: meta.isOpened || false,
      activationType,
    },
    author: {
      id: authorId,
      name: authorName,
      avatar: authorAvatar,
      isAdministrator: authorIsAdmin,
      is_followed: authorIsFollowed,
    },
    tree: apiCapsule.tree ? { id: apiCapsule.tree } : undefined,
    engagement: {
      comments: commentsCount,
    },
    privacy: {
      level: privacyLevel ?? 'public',
      allowedUserIds: allowedUserIds ?? [],
      coauthors: normalizedCoauthors,
    },
  };
}

export interface CapsuleSearchApiAuthor {
  id?: string | number;
  name?: string;
  avatar?: string | null;
  is_followed?: boolean;
}

export interface CapsuleSearchApiItem {
  id: string | number;
  title?: string;
  author?: string | number | CapsuleSearchApiAuthor | null;
}

export interface CapsuleSearchResponse {
  capsules: CapsuleSearchApiItem[];
  offset?: number;
}

function mapCapsuleSearchItemToCapsule(item: CapsuleSearchApiItem): Capsule {
  const authorRaw = item.author;
  let authorId = '';
  let authorName = '';
  let authorAvatar: string | undefined;
  let isFollowed: boolean | undefined;

  if (authorRaw && typeof authorRaw === 'object') {
    authorId = String(authorRaw.id ?? '');
    authorName = authorRaw.name ?? '';
    authorAvatar = authorRaw.avatar ?? undefined;
    isFollowed = authorRaw.is_followed;
  } else if (authorRaw !== undefined && authorRaw !== null) {
    authorId = String(authorRaw);
  }

  return {
    id: String(item.id),
    title: item.title ?? '',
    content: [],
    meta: {
      createdAt: '',
      isOpened: false,
    },
    author: {
      id: authorId,
      name: authorName,
      avatar: authorAvatar,
      is_followed: isFollowed,
    },
    engagement: { comments: 0 },
    privacy: { level: 'public' },
  };
}

export const capsulesApi = {
  async getCapsules(params?: {
    page?: number;
    limit?: number;
    authorId?: string;
    privacy?: 'public' | 'coauthors' | 'selected';
    isOpened?: boolean;
    isClosed?: boolean;
  }): Promise<ApiResponse<CapsuleListResponse>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.authorId) queryParams.append('authorId', params.authorId);
    if (params?.privacy) queryParams.append('privacy', params.privacy);
    if (params?.isOpened !== undefined)
      queryParams.append('isOpened', String(params.isOpened));
    if (params?.isClosed !== undefined)
      queryParams.append('isClosed', String(params.isClosed));

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/capsules/?${queryString}` : '/capsules/';

    const response = await apiClient.get<any>(endpoint);

    if (response.success && response.data) {
      let apiCapsules: CapsuleApiResponse[] = [];
      let total = 0;
      let hasMore = false;
      let page = params?.page || 1;
      let limit = params?.limit || 20;

      if (Array.isArray(response.data.results)) {
        apiCapsules = response.data.results;
        total = response.data.count || 0;
        hasMore = !!response.data.next;
      } else if (Array.isArray(response.data.capsules)) {
        const rawCapsules = response.data.capsules;
        apiCapsules = rawCapsules.map((capsule: any) => {
          const author =
            typeof capsule.author === 'object' && capsule.author
              ? capsule.author
              : typeof capsule.author === 'string'
                ? capsule.author
                : capsule.author?.id
                  ? {
                      id: String(capsule.author.id),
                      name: '',
                      avatar: null,
                      is_followed: false,
                    }
                  : '';

          let metaData: any = {};

          if (capsule.meta) {
            if (typeof capsule.meta === 'string') {
              try {
                metaData = JSON.parse(capsule.meta);
              } catch (e) {
                metaData = {};
              }
            } else {
              metaData = { ...capsule.meta };
            }
          }

          if (
            capsule.scheduled_at &&
            !metaData.scheduledAt &&
            !metaData.scheduled_at
          ) {
            metaData.scheduledAt = capsule.scheduled_at;
          }
          if (
            capsule.isOpened !== undefined &&
            metaData.isOpened === undefined
          ) {
            metaData.isOpened = capsule.isOpened;
          }
          if (capsule.firstOpenedAt && !metaData.firstOpenedAt) {
            metaData.firstOpenedAt = capsule.firstOpenedAt;
          }
          if (capsule.createdAt && !metaData.createdAt) {
            metaData.createdAt = capsule.createdAt;
          }

          return {
            id: capsule.id || '',
            title: capsule.title || '',
            description: capsule.description,
            image: capsule.image,
            imagePreview: capsule.imagePreview,
            content: capsule.content,
            scenario: capsule.scenario,
            meta: metaData,
            author,
            tree: capsule.tree,
            comment_count:
              typeof capsule.comment_count === 'number'
                ? capsule.comment_count
                : undefined,
            engagement: capsule.engagement,
            privacy: capsule.privacy || 'public',
            activation_policy: capsule.activation_policy,
          } as CapsuleApiResponse;
        });
        total = response.data.total || response.data.count || 0;
        hasMore = response.data.hasMore || false;
        page = response.data.page || page;
        limit = response.data.limit || limit;
      }

      const capsules = Array.isArray(apiCapsules)
        ? apiCapsules.map(mapApiCapsuleToCapsule)
        : [];

      return {
        success: true,
        data: {
          capsules,
          total,
          page,
          limit,
          hasMore,
        },
      };
    }

    if (response.success) {
      return {
        success: true,
        data: {
          capsules: [],
          total: 0,
          page: params?.page || 1,
          limit: params?.limit || 20,
          hasMore: false,
        },
      };
    }

    return response as any;
  },

  async searchCapsules(params: {
    q: string;
    limit?: number;
  }): Promise<ApiResponse<{ capsules: Capsule[] }>> {
    const queryParams = new URLSearchParams();
    if (params.q) queryParams.append('query', params.q);
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    const queryString = queryParams.toString();
    const endpoint = `/capsules/search/${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<CapsuleSearchResponse>(endpoint);

    if (!response.success || !response.data) {
      return response as any;
    }

    return {
      success: true,
      data: {
        capsules: response.data.capsules.map(mapCapsuleSearchItemToCapsule),
      },
    };
  },

  async getCapsule(id: string | number): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.get<CapsuleApiResponse>(
      `/capsules/${id}/`
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },

  async createCapsule(
    data: CreateCapsuleRequest
  ): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.post<CapsuleApiResponse>(
      '/capsules/',
      data
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },

  async updateCapsule(
    id: string | number,
    data: UpdateCapsuleRequest
  ): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.patch<CapsuleApiResponse>(
      `/capsules/${id}/`,
      data
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },

  async activateCapsule(id: string | number): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.post<CapsuleApiResponse>(
      `/capsules/${id}/activate/`,
      {}
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },

  async posthumousApprove(
    id: string | number
  ): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.post<{ success: boolean }>(
      `/capsules/${id}/posthumous-approve/`,
      {}
    );
  },

  async updateAllowedUsers(
    id: string | number,
    body: { allowedUserIds?: number[]; allowedUserEmails?: string[] }
  ): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.post<CapsuleApiResponse>(
      `/capsules/${id}/allowed-users/`,
      body
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },

  async updateCoauthors(
    id: string | number,
    data: { coauthorsIds?: number[]; coauthorsEmails?: string[] }
  ): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.post<CapsuleApiResponse>(
      `/capsules/${id}/coauthors/`,
      data
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },

  async removeCoauthor(
    id: string | number,
    userId: string | number
  ): Promise<ApiResponse<{}>> {
    return apiClient.delete<{}>(`/capsules/${id}/coauthors/${userId}/`);
  },

  async reinviteCoauthor(
    id: string | number,
    userId: string | number
  ): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.post<CapsuleApiResponse>(
      `/capsules/${id}/coauthors/${userId}/reinvite/`,
      {}
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },

  async getCapsuleInvites(
    id: string | number
  ): Promise<ApiResponse<CapsuleInvite[]>> {
    return apiClient.get<CapsuleInvite[]>(`/capsules/${id}/invites/`);
  },

  async inviteByEmail(
    capsuleId: string | number,
    email: string,
    role: 'coauthor' | 'viewer' = 'coauthor'
  ): Promise<ApiResponse<{ message?: string }>> {
    return apiClient.post<{ message?: string }>(
      `/capsules/${capsuleId}/invite-by-email/`,
      { email, role }
    );
  },

  async createCoauthorReferralLink(capsuleId: string | number): Promise<
    ApiResponse<{
      success: boolean;
      referralToken: string;
      referralLink: string;
    }>
  > {
    return apiClient.post<{
      success: boolean;
      referralToken: string;
      referralLink: string;
    }>(`/capsules/${capsuleId}/coauthors/referral-link/`, {});
  },

  async getPurchasedScenarios(
    id: string | number
  ): Promise<ApiResponse<{ purchased_scenarios: string[] }>> {
    return apiClient.get<{ purchased_scenarios: string[] }>(
      `/capsules/${id}/purchased-scenarios/`
    );
  },

  async deleteCapsule(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>(`/capsules/${id}`);
  },

  async openCapsule(id: string): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.post<CapsuleApiResponse>(
      `/capsules/${id}/activate/`,
      {}
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },

  async addContent(
    id: string,
    content: Omit<ContentItem, 'id' | 'order'>
  ): Promise<ApiResponse<ContentItem>> {
    return apiClient.post<ContentItem>(`/capsules/${id}/content`, content);
  },

  async updateContent(
    capsuleId: string,
    contentId: string,
    content: Partial<ContentItem>
  ): Promise<ApiResponse<ContentItem>> {
    return apiClient.put<ContentItem>(
      `/capsules/${capsuleId}/content/${contentId}`,
      content
    );
  },

  async deleteContent(
    capsuleId: string,
    contentId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>(
      `/capsules/${capsuleId}/content/${contentId}`
    );
  },

  async uploadFile(
    file: File,
    capsuleId: string
  ): Promise<ApiResponse<{ url: string; filename: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('capsuleId', capsuleId);

    return apiClient.upload<{ url: string; filename: string }>(
      `/capsules/${capsuleId}/upload`,
      formData
    );
  },

  async getComments(
    capsuleId: string,
    params?: { page?: number; limit?: number; parentId?: string }
  ): Promise<ApiResponse<CommentListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.parentId) queryParams.append('parentId', params.parentId);
    const query = queryParams.toString();
    return apiClient.get<CommentListResponse>(
      `/capsules/${capsuleId}/comments${query ? `?${query}` : ''}`
    );
  },

  async addComment(
    capsuleId: string | number,
    data: CreateCommentRequest
  ): Promise<ApiResponse<Comment>> {
    return apiClient.post<Comment>(`/capsules/${capsuleId}/comments/`, {
      text: data.text,
      position: data.position,
      ...(data.comment_object != null && {
        comment_object: data.comment_object,
      }),
    });
  },

  async addCommentReply(
    capsuleId: string | number,
    commentId: string | number,
    data: { text: string; position?: { top: number; left: number } }
  ): Promise<ApiResponse<Comment>> {
    return apiClient.post<Comment>(
      `/capsules/${capsuleId}/comments/${commentId}/replies/`,
      data
    );
  },

  async updateComment(
    capsuleId: string,
    commentId: string,
    data: UpdateCommentRequest
  ): Promise<ApiResponse<Comment>> {
    return apiClient.put<Comment>(
      `/capsules/${capsuleId}/comments/${commentId}`,
      data
    );
  },

  async deleteComment(
    capsuleId: string,
    commentId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>(
      `/capsules/${capsuleId}/comments/${commentId}`
    );
  },

  async getCapsuleTree(capsuleId: string): Promise<ApiResponse<Tree>> {
    return apiClient.get<Tree>(`/capsules/${capsuleId}/tree`);
  },

  async createCapsuleTree(
    capsuleId: string,
    data: CreateTreeRequest
  ): Promise<ApiResponse<Tree>> {
    return apiClient.post<Tree>(`/capsules/${capsuleId}/tree`, data);
  },

  async plantCapsuleTree(capsuleId: string): Promise<ApiResponse<Tree>> {
    return apiClient.post<Tree>(`/capsules/${capsuleId}/tree/plant`);
  },

  async donateToCapsuleTree(
    capsuleId: string,
    donation: DonationRequest
  ): Promise<ApiResponse<Donation>> {
    return apiClient.post<Donation>(
      `/capsules/${capsuleId}/tree/donate`,
      donation
    );
  },

  async purchaseScenario(
    capsuleId: string,
    scenarioId: string
  ): Promise<ApiResponse<Capsule>> {
    const response = await apiClient.post<CapsuleApiResponse>(
      `/capsules/${capsuleId}/scenarios/${scenarioId}/purchase/`,
      {}
    );
    if (response.success && response.data) {
      return {
        success: true,
        data: mapApiCapsuleToCapsule(response.data),
      };
    }
    return response as any;
  },
};
